import { ChangeEvent, ReactNode } from 'react'

import {
  Checkbox,
  Modal,
  ModalProvider,
  PrimaryButton,
  SecondaryButton,
  SquareDotLoadingIndicator,
} from '@joor/design-system'
import styled from 'styled-components'

import usePortal from 'hooks/usePortal'

import { ActionsWrapper, ButtonsWrapper, KeyframeModal } from '../common.ui'
import {
  AccountOption as AccountOptionComponent,
  AccountOptionType,
} from './AccountOption/AccountOption'

type OkButtonProps = {
  label: string
  loading?: boolean
  disabled?: boolean
  onClick: () => void
}

type CancelButtonProps = {
  disabled?: boolean
  label: string
}

interface AccountsSelectionModalProps {
  title: string
  subtitle?: string
  headingContent?: ReactNode
  isOpen: boolean
  onClose: () => void
  isLoading?: boolean
  onCheckboxChange?: (
    event: ChangeEvent<HTMLInputElement>,
    userAccountId: string,
  ) => void
  okButton?: OkButtonProps
  cancelButton?: CancelButtonProps
  leftExtraAction?: ReactNode
  accountOptions: AccountOption[]
}

interface AccountOption extends AccountOptionType {
  id: string
  disableOptionCheckbox: boolean
  optionChecked: boolean
}

/**
 * A generic function that maps an array of any type to an array of AccountOptionType options.
 * @param items - The input array of a generic type T.
 * @param mapper - A function that defines how to convert an item of type T to an AccountOptionType.
 * @returns An array of AccountOption.
 */
export function mapToAccountOptions<T>(
  items: T[],
  mapper: (item: T) => AccountOption,
): AccountOption[] {
  return items.map(mapper)
}

export const DataTestIds = {
  Loader: 'square-loader',
  AccountRow: 'account-row-',
  AccountCheckbox: 'account-checkbox-',
  OkButtonLoading: 'ok-button-loading',
} as const

/**
 * A modal that allows the user to select <b>multiple accounts.</b>
 *
 * <b>Just important params are described below.</b>
 * @param title
 * @param subtitle
 * @param headingContent - The content to display above the account list.
 * @param isOpen
 * @param onClose
 * @param isLoading
 * @param onCheckboxChange
 * @param okButton
 * @param cancelButton
 * @param leftExtraAction - The content to display on the left side of the action bar.
 * @param accountOptions - An array of `AccountOption`. Consumer code must
 * map its fetched data to this format using the `mapToAccountOptions` function exported
 * from this modal file.
 */
export const AccountsSelectionModal = ({
  title,
  subtitle,
  headingContent,
  isOpen,
  onClose,
  isLoading = false,
  onCheckboxChange,
  okButton,
  cancelButton,
  leftExtraAction,
  accountOptions,
}: AccountsSelectionModalProps) => {
  const Portal = usePortal()

  return (
    <Portal>
      <ModalProvider>
        <KeyframeModal
          isOpen={isOpen}
          onClose={onClose}
          title={{ text: title }}
          subtitle={subtitle}
          size="medium"
        >
          <Modal.Content>
            {isLoading ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: 290,
                }}
              >
                <SquareDotLoadingIndicator data-testid={DataTestIds.Loader} />
              </div>
            ) : (
              <>
                {headingContent && headingContent}
                <AccountsWrapper>
                  {accountOptions?.length === 0 ? (
                    <div
                      style={{
                        height: 200,
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      No applicable accounts found.
                    </div>
                  ) : (
                    <>
                      {accountOptions?.map((option) => {
                        const {
                          id,
                          disableOptionCheckbox,
                          optionChecked,
                          ...accountOption
                        } = option

                        return (
                          <AccountContainer
                            key={id}
                            data-testid={`${DataTestIds.AccountRow}${id}`}
                          >
                            <StyledCheckbox
                              id={id}
                              data-testid={`${DataTestIds.AccountCheckbox}${id}`}
                              onChange={(event) =>
                                onCheckboxChange?.(event, id)
                              }
                              size="regular"
                              checked={optionChecked}
                              disabled={disableOptionCheckbox}
                            />
                            <AccountOptionComponent {...accountOption} />
                          </AccountContainer>
                        )
                      })}
                    </>
                  )}
                </AccountsWrapper>
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <ActionsWrapper>
              {leftExtraAction ? leftExtraAction : <div />}
              <ButtonsWrapper>
                {cancelButton && (
                  <SecondaryButton
                    disabled={isLoading || cancelButton?.disabled}
                    onClick={onClose}
                  >
                    {cancelButton.label}
                  </SecondaryButton>
                )}
                {okButton && (
                  <PrimaryButton
                    data-testid={
                      okButton.loading ? DataTestIds.OkButtonLoading : undefined
                    }
                    disabled={isLoading || okButton.disabled}
                    loading={okButton.loading}
                    onClick={okButton.onClick}
                  >
                    {okButton.label}
                  </PrimaryButton>
                )}
              </ButtonsWrapper>
            </ActionsWrapper>
          </Modal.Actions>
        </KeyframeModal>
      </ModalProvider>
    </Portal>
  )
}

const AccountsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  margin-top: 10px;
  max-height: 330px;
  overflow-y: auto;
`

const AccountContainer = styled.div`
  display: flex;
  align-items: center;
`

const StyledCheckbox = styled(Checkbox)`
  margin-right: 10px;
`
